/**
 * Sound Alert System for DentalPulse
 * 
 * Generates pleasant chime sounds using Web Audio API.
 * Each sound is designed to be distinctive but professional for a dental office setting.
 */

// Audio context singleton
let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
};

// Sound definitions - 10 different pleasant chimes
const SOUND_DEFINITIONS = {
  gentle_chime: {
    name: 'Gentle Chime',
    description: 'Soft, single tone chime',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime); // A5
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5);
      osc.connect(gainNode);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    }
  },
  
  double_chime: {
    name: 'Double Chime',
    description: 'Two-note ascending chime',
    play: (ctx, gainNode) => {
      [523.25, 659.25].forEach((freq, i) => { // C5, E5
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gainNode);
        const startTime = ctx.currentTime + i * 0.15;
        gainNode.gain.setValueAtTime(0.25, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.4);
        osc.start(startTime);
        osc.stop(startTime + 0.4);
      });
    }
  },
  
  soft_bell: {
    name: 'Soft Bell',
    description: 'Warm bell-like tone',
    play: (ctx, gainNode) => {
      const frequencies = [523.25, 1046.5, 1318.51]; // C5, C6, E6
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.2 / (i + 1);
        osc.connect(oscGain);
        oscGain.connect(gainNode);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.8);
      });
    }
  },
  
  bright_ding: {
    name: 'Bright Ding',
    description: 'Clear, bright notification',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(1760, ctx.currentTime); // A6
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
      osc.connect(gainNode);
      gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  },
  
  calm_pulse: {
    name: 'Calm Pulse',
    description: 'Soothing pulsating tone',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      const lfo = ctx.createOscillator();
      const lfoGain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = 440; // A4
      lfo.type = 'sine';
      lfo.frequency.value = 6;
      lfoGain.gain.value = 0.1;
      
      lfo.connect(lfoGain);
      lfoGain.connect(gainNode.gain);
      osc.connect(gainNode);
      
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      
      osc.start(ctx.currentTime);
      lfo.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
      lfo.stop(ctx.currentTime + 0.6);
    }
  },
  
  melodic_trio: {
    name: 'Melodic Trio',
    description: 'Three-note melody',
    play: (ctx, gainNode) => {
      [392, 523.25, 659.25].forEach((freq, i) => { // G4, C5, E5
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gainNode);
        const startTime = ctx.currentTime + i * 0.12;
        gainNode.gain.setValueAtTime(0.22, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.35);
        osc.start(startTime);
        osc.stop(startTime + 0.35);
      });
    }
  },
  
  alert_ping: {
    name: 'Alert Ping',
    description: 'Attention-getting ping',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1318.51, ctx.currentTime); // E6
      osc.connect(gainNode);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
      
      // Second ping
      setTimeout(() => {
        const ctx2 = getAudioContext();
        const gainNode2 = ctx2.createGain();
        gainNode2.connect(ctx2.destination);
        const osc2 = ctx2.createOscillator();
        osc2.type = 'sine';
        osc2.frequency.value = 1567.98; // G6
        osc2.connect(gainNode2);
        gainNode2.gain.setValueAtTime(0.25, ctx2.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.001, ctx2.currentTime + 0.2);
        osc2.start(ctx2.currentTime);
        osc2.stop(ctx2.currentTime + 0.2);
      }, 150);
    }
  },
  
  warm_glow: {
    name: 'Warm Glow',
    description: 'Rich, warm tone',
    play: (ctx, gainNode) => {
      const frequencies = [261.63, 329.63, 392]; // C4, E4, G4 (C major)
      frequencies.forEach((freq) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const oscGain = ctx.createGain();
        oscGain.gain.value = 0.15;
        osc.connect(oscGain);
        oscGain.connect(gainNode);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.7);
      });
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    }
  },
  
  crystal_clear: {
    name: 'Crystal Clear',
    description: 'High, crystal-like tone',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      osc.type = 'sine';
      osc2.type = 'sine';
      osc.frequency.value = 2093; // C7
      osc2.frequency.value = 2637; // E7
      osc.connect(gainNode);
      osc2.connect(gainNode);
      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc2.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
      osc2.stop(ctx.currentTime + 0.4);
    }
  },
  
  gentle_wave: {
    name: 'Gentle Wave',
    description: 'Soft sweeping tone',
    play: (ctx, gainNode) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(349.23, ctx.currentTime); // F4
      osc.frequency.linearRampToValueAtTime(523.25, ctx.currentTime + 0.3); // to C5
      osc.frequency.linearRampToValueAtTime(440, ctx.currentTime + 0.6); // to A4
      osc.connect(gainNode);
      gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.25, ctx.currentTime + 0.3);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.7);
    }
  },

  read_receipt: {
    name: 'Read Receipt',
    description: 'Quick confirmation ping for read receipts',
    play: (ctx, gainNode) => {
      // Short, subtle ping - like a text message read receipt
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1046.50, ctx.currentTime); // C6
      osc.frequency.exponentialRampToValueAtTime(1318.51, ctx.currentTime + 0.08); // to E6
      osc.connect(gainNode);
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.15);
    }
  }
};

// Ordered list of sounds for selection UI
export const AVAILABLE_SOUNDS = [
  { id: 'none', name: 'None (Silent)', description: 'No sound' },
  { id: 'gentle_chime', ...SOUND_DEFINITIONS.gentle_chime },
  { id: 'double_chime', ...SOUND_DEFINITIONS.double_chime },
  { id: 'soft_bell', ...SOUND_DEFINITIONS.soft_bell },
  { id: 'bright_ding', ...SOUND_DEFINITIONS.bright_ding },
  { id: 'calm_pulse', ...SOUND_DEFINITIONS.calm_pulse },
  { id: 'melodic_trio', ...SOUND_DEFINITIONS.melodic_trio },
  { id: 'alert_ping', ...SOUND_DEFINITIONS.alert_ping },
  { id: 'warm_glow', ...SOUND_DEFINITIONS.warm_glow },
  { id: 'crystal_clear', ...SOUND_DEFINITIONS.crystal_clear },
  { id: 'gentle_wave', ...SOUND_DEFINITIONS.gentle_wave },
  { id: 'read_receipt', ...SOUND_DEFINITIONS.read_receipt }
];

// Alert types that can have sounds
export const ALERT_TYPES_WITH_SOUNDS = [
  { id: 'patient_arrived', name: 'Patient Arrived', description: 'When a patient arrives at the office' },
  { id: 'dr_needed', name: 'Dr. Needed', description: 'When a doctor is requested' },
  { id: 'help_needed', name: 'Help Needed', description: 'When help is requested' },
  { id: 'read_receipt', name: 'Read Receipt', description: 'When someone reads your alert' },
  { id: 'custom_button_1', name: 'Custom Button 1', description: 'First custom button' },
  { id: 'custom_button_2', name: 'Custom Button 2', description: 'Second custom button' },
  { id: 'custom_button_3', name: 'Custom Button 3', description: 'Third custom button' },
  { id: 'custom_button_4', name: 'Custom Button 4', description: 'Fourth custom button' },
  { id: 'custom_button_5', name: 'Custom Button 5', description: 'Fifth custom button' },
  { id: 'custom_button_6', name: 'Custom Button 6', description: 'Sixth custom button' },
  { id: 'message', name: 'New Message', description: 'When a new message is received' }
];

// Default sound assignments
export const DEFAULT_SOUND_SETTINGS = {
  sounds_enabled: true,
  sound_volume: 0.7,
  sound_assignments: {
    patient_arrived: 'gentle_chime',
    dr_needed: 'alert_ping',
    help_needed: 'melodic_trio',
    read_receipt: 'read_receipt',
    custom_button_1: 'double_chime',
    custom_button_2: 'soft_bell',
    custom_button_3: 'bright_ding',
    custom_button_4: 'calm_pulse',
    custom_button_5: 'warm_glow',
    custom_button_6: 'crystal_clear',
    message: 'gentle_wave'
  }
};

/**
 * Play a sound by its ID
 * @param {string} soundId - The ID of the sound to play
 * @param {number} volume - Volume level (0-1), defaults to 0.7
 */
export const playSound = (soundId, volume = 0.7) => {
  if (!soundId || soundId === 'none') return;
  
  const soundDef = SOUND_DEFINITIONS[soundId];
  if (!soundDef) {
    console.warn(`Unknown sound: ${soundId}`);
    return;
  }
  
  try {
    const ctx = getAudioContext();
    const gainNode = ctx.createGain();
    gainNode.connect(ctx.destination);
    gainNode.gain.value = Math.min(1, Math.max(0, volume));
    
    soundDef.play(ctx, gainNode);
  } catch (error) {
    console.error('Failed to play sound:', error);
  }
};

/**
 * Play sound for a specific alert type based on practice settings
 * @param {string} alertType - The type of alert (patient_arrived, dr_needed, etc.)
 * @param {object} soundSettings - The practice sound settings
 */
export const playSoundForAlert = (alertType, soundSettings) => {
  if (!soundSettings?.sounds_enabled) return;
  
  const soundId = soundSettings?.sound_assignments?.[alertType];
  const volume = soundSettings?.sound_volume ?? 0.7;
  
  playSound(soundId, volume);
};

/**
 * Preview a sound (for admin settings)
 * @param {string} soundId - The ID of the sound to preview
 */
export const previewSound = (soundId) => {
  playSound(soundId, 0.7);
};

/**
 * Initialize audio context on user interaction (required by browsers)
 * Call this on first user click/tap
 */
export const initializeAudio = () => {
  try {
    const ctx = getAudioContext();
    // Create a silent buffer to unlock audio
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    return true;
  } catch (error) {
    console.error('Failed to initialize audio:', error);
    return false;
  }
};
